import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X, CheckCircle, Gear } from "@phosphor-icons/react";

const STORAGE_KEY = "acta-cookie-consent-v1";

/**
 * Read consent from localStorage.
 * Returns null when no choice was made yet.
 */
export function getConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/**
 * Convenience: was a given category accepted?
 * Essentials are always on.
 */
export function hasConsent(category) {
  if (category === "essential") return true;
  const c = getConsent();
  return !!(c && c[category] === true);
}

function persist(consent) {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...consent, essential: true, ts: new Date().toISOString() })
    );
  } catch {}
  // Notify the rest of the app
  window.dispatchEvent(new CustomEvent("acta-consent-change", { detail: { ...consent, essential: true } }));
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false });

  useEffect(() => {
    // Wait one tick so the page doesn't shift visually before hydration
    const t = setTimeout(() => {
      if (getConsent() === null) setVisible(true);
    }, 600);
    return () => clearTimeout(t);
  }, []);

  if (!visible) return null;

  const acceptAll = () => {
    persist({ analytics: true });
    setVisible(false);
  };
  const essentialsOnly = () => {
    persist({ analytics: false });
    setVisible(false);
  };
  const savePrefs = () => {
    persist(prefs);
    setVisible(false);
  };

  return (
    <div
      className="fixed z-[60] bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-md au-rise"
      role="dialog"
      data-acta-modal-kind="banner"
      aria-live="polite"
      aria-label="Préférences de cookies"
      data-testid="cookie-banner"
    >
      <div
        className="relative overflow-hidden rounded-[16px] border shadow-[var(--do-shadow-card)]"
        style={{
          background: "rgba(255,255,255,0.96)",
          backdropFilter: "blur(20px) saturate(140%)",
          borderColor: "rgba(29,78,216,0.20)",
        }}
      >
        {/* halo */}
        <div
          className="pointer-events-none absolute -top-10 -right-10 h-32 w-32 rounded-full blur-2xl opacity-40"
          style={{ background: "radial-gradient(circle, #60A5FA, transparent 70%)" }}
        />

        <button
          onClick={essentialsOnly}
          className="absolute top-3 right-3 p-1 rounded-md text-[var(--do-muted)] hover:text-[var(--do-ink)] transition-colors"
          aria-label="Fermer (essentiels uniquement)"
          data-testid="cookie-close"
        >
          <X size={16} weight="bold" />
        </button>

        <div className="relative p-5">
          <div className="flex items-start gap-3">
            <div
              className="h-9 w-9 rounded-[10px] shrink-0 flex items-center justify-center"
              style={{ background: "linear-gradient(135deg, #2563EB, #1E3A8A)" }}
            >
              <Cookie size={18} weight="fill" className="text-[var(--do-on-accent)]" />
            </div>
            <div className="min-w-0">
              <div className="font-heading font-bold text-[14px] text-[var(--do-ink)]">Cookies & confidentialité</div>
              <p className="text-[12.5px] text-[var(--do-muted)] mt-1 leading-relaxed">
                Nous utilisons des cookies <strong>essentiels</strong> (session, thème) — toujours actifs. Vous pouvez
                accepter des <strong>cookies analytiques</strong> anonymes pour nous aider à améliorer le service.
                Détails dans notre{" "}
                <Link to="/legal/confidentialite" className="text-[var(--do-accent)] font-semibold hover:text-[var(--do-ink)] underline underline-offset-2">
                  politique de confidentialité
                </Link>
                .
              </p>
            </div>
          </div>

          {showPrefs && (
            <div className="mt-4 pt-4 border-t border-[var(--do-border)] space-y-2.5" data-testid="cookie-prefs">
              <div className="flex items-start justify-between gap-3 text-[12px]">
                <div>
                  <div className="font-heading font-bold text-[var(--do-ink)]">Essentiels</div>
                  <div className="text-[var(--do-muted)]">Authentification, session, préférence de thème.</div>
                </div>
                <span className="text-[10px] font-mono-stat uppercase tracking-wider text-[var(--do-accent)] bg-[var(--do-accent-soft)] border border-[var(--do-border)] px-2 py-1 rounded-full">
                  Toujours actif
                </span>
              </div>
              <label className="flex items-start justify-between gap-3 text-[12px] cursor-pointer">
                <div>
                  <div className="font-heading font-bold text-[var(--do-ink)]">Analytiques anonymes</div>
                  <div className="text-[var(--do-muted)]">Pages visitées, durée, agrégés sans identification.</div>
                </div>
                <input
                  type="checkbox"
                  checked={prefs.analytics}
                  onChange={(e) => setPrefs((p) => ({ ...p, analytics: e.target.checked }))}
                  data-testid="cookie-pref-analytics"
                  className="mt-1 h-4 w-4 accent-[var(--do-accent)] cursor-pointer"
                />
              </label>
            </div>
          )}

          <div className="mt-4 flex flex-wrap gap-2">
            {showPrefs ? (
              <>
                <button
                  onClick={savePrefs}
                  data-testid="cookie-save"
                  className="au-btn-primary px-4 py-2 text-[12px] inline-flex items-center gap-1.5 flex-1 justify-center"
                >
                  <CheckCircle size={13} weight="fill" /> Enregistrer
                </button>
                <button
                  onClick={() => setShowPrefs(false)}
                  className="px-3 py-2 text-[12px] font-semibold text-[var(--do-muted)] hover:text-[var(--do-ink)]"
                >
                  Annuler
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={acceptAll}
                  data-testid="cookie-accept-all"
                  className="au-btn-primary px-4 py-2 text-[12px] inline-flex items-center gap-1.5 flex-1 justify-center"
                >
                  <CheckCircle size={13} weight="fill" /> Tout accepter
                </button>
                <button
                  onClick={essentialsOnly}
                  data-testid="cookie-essentials-only"
                  className="px-3 py-2 text-[12px] font-semibold rounded-[10px] border border-[var(--do-border)] text-[var(--do-ink)] hover:border-[var(--do-border)] hover:text-[var(--do-accent)]"
                >
                  Essentiels uniquement
                </button>
                <button
                  onClick={() => setShowPrefs(true)}
                  data-testid="cookie-customize"
                  className="px-2 py-2 text-[12px] font-semibold text-[var(--do-muted)] hover:text-[var(--do-ink)] inline-flex items-center gap-1"
                >
                  <Gear size={12} weight="bold" /> Personnaliser
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
